import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Heart,
  Users,
  Award,
  MapPin,
  Clock,
  Shield,
  Target,
  Star,
  Accessibility,
  Car,
  DoorOpen,
  Move
} from 'lucide-react';
import { pharmacyInfo, teamMembers, pharmacyStats, accessibilityFeatures } from '../mock';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  À Propos de <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Notre Pharmacie</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Depuis notre ouverture, nous nous engageons à fournir des soins pharmaceutiques 
                  de qualité supérieure dans un environnement moderne et accessible.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {pharmacyStats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      {stat.icon === 'Award' && <Award className="h-8 w-8 text-teal-600 mx-auto mb-3" />}
                      {stat.icon === 'Users' && <Users className="h-8 w-8 text-teal-600 mx-auto mb-3" />}
                      {stat.icon === 'Star' && <Star className="h-8 w-8 text-teal-600 mx-auto mb-3" />}
                      {stat.icon === 'Heart' && <Heart className="h-8 w-8 text-teal-600 mx-auto mb-3" />}
                      <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1752738534033-9dd5b4d0148c" 
                  alt="Intérieur moderne de notre pharmacie"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Notre Mission & Valeurs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous croyons qu'une pharmacie moderne doit allier expertise professionnelle et accessibilité
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Soin & Bienveillance
                </h3>
                <p className="text-gray-600">
                  Chaque client est unique. Nous prenons le temps d'écouter et de conseiller 
                  avec attention et professionnalisme.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Qualité & Sécurité
                </h3>
                <p className="text-gray-600">
                  Nos standards élevés garantissent la qualité de nos produits et la sécurité 
                  de nos conseils pharmaceutiques.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Accessibilité & Innovation
                </h3>
                <p className="text-gray-600">
                  Une pharmacie moderne accessible à tous, avec des services innovants 
                  pour répondre aux besoins d'aujourd'hui.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Notre Équipe Professionnelle
            </h2>
            <p className="text-xl text-gray-600">
              Des pharmaciens expérimentés à votre service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="bg-white border-0 shadow-lg overflow-hidden">
                <div className="flex">
                  <div className="w-1/3">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <CardContent className="w-2/3 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-teal-600 font-medium mb-2">
                      {member.role}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      {member.experience}
                    </p>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-700">Spécialités:</p>
                      {member.specialties.map((specialty, idx) => (
                        <span key={idx} className="inline-block bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded mr-2">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Accessibilité Totale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre pharmacie est conçue pour être accessible à tous, avec des aménagements 
              spécialement pensés pour les personnes à mobilité réduite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {accessibilityFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                    {feature.icon === 'Car' && <Car className="h-8 w-8 text-white" />}
                    {feature.icon === 'DoorOpen' && <DoorOpen className="h-8 w-8 text-white" />}
                    {feature.icon === 'Move' && <Move className="h-8 w-8 text-white" />}
                    {feature.icon === 'Accessibility' && <Accessibility className="h-8 w-8 text-white" />}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <Accessibility className="h-12 w-12 text-teal-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Certification Accessibilité
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Notre pharmacie respecte toutes les normes d'accessibilité suisses et accueille 
              chaque jour des clients à mobilité réduite dans les meilleures conditions.
            </p>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white">
              En Savoir Plus
            </Button>
          </div>
        </div>
      </section>

      {/* Location Highlight */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Idéalement Située à Meyrin-Vergers
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-6 w-6 text-teal-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Face à l'Hôpital</h3>
                    <p className="text-gray-600">
                      Position stratégique pour un accès rapide aux soins pharmaceutiques
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-teal-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Quartier en Développement</h3>
                    <p className="text-gray-600">
                      Au cœur du nouveau quartier des Vergers, zone moderne et dynamique
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Car className="h-6 w-6 text-teal-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Parking Accessible</h3>
                    <p className="text-gray-600">
                      Places de stationnement dédiées et accessibles à tous
                    </p>
                  </div>
                </div>
              </div>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" size="lg">
                <MapPin className="h-5 w-5 mr-2" />
                Voir l'Itinéraire
              </Button>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="aspect-square bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="h-24 w-24 text-teal-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Meyrin-Vergers
                    </h3>
                    <p className="text-gray-600">
                      Quartier moderne et accessible
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;